/**
 * Site header with navigation, auth actions, and responsive menu.
 */
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { signIn, signOut } from "../scripts/api";
import Button from "./ui/Button";

const DEFAULT_AVATAR_URL = "https://pax-studios.org/images/avatars/g.png";

function MenuIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
    </svg>
  );
}

export default function Header({ session, loading }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const isAuthenticated = !!session?.authenticated;
  const user = session?.user || null;

  const displayName =
    session?.displayName ||
    user?.alias?.trim() ||
    (user?.email?.includes("@") ? user.email.split("@")[0] : user?.email) ||
    "";

  const avatarUrl = user?.avatar_url?.trim() || DEFAULT_AVATAR_URL;

  const handleSignIn = () => {
    setMenuOpen(false);
    signIn();
  };

  const handleSignOut = () => {
    setMenuOpen(false);
    signOut();
  };

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-black/25 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-3 py-2 sm:px-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex min-w-0 cursor-pointer items-center gap-3 rounded-lg border border-transparent p-1 text-left outline-none hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-sky-400/70"
          >
            <img
              src="/logo-min.png"
              alt=""
              //className="h-8 w-8 shrink-0 rounded border border-dashed border-white/45 bg-white/5"
              className="h-8 w-8 shrink-0 rounded"
            />
            <span className="truncate text-lg font-extrabold tracking-wide text-white">
              Pax Studios
            </span>
          </button>

          <nav className="hidden items-center gap-2 sm:flex">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/contact")}
              className="h-10 min-h-10 shrink-0 font-semibold"
            >
              Contact
            </Button>
          </nav>
        </div>

        <div className="hidden items-center gap-2 sm:flex">
          {loading ? null : isAuthenticated ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/profile")}
                className="h-10 min-h-10 max-w-[16rem] shrink-0 font-semibold"
              >
                <span className="flex max-w-full items-center gap-2">
                  <img
                    src={avatarUrl}
                    alt=""
                    className="h-6 w-6 shrink-0 rounded-full border border-white/20 object-cover"
                  />
                  <span className="truncate">{displayName}</span>
                </span>
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/membership")}
                className="h-10 min-h-10 shrink-0 font-semibold"
              >
                Membership
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleSignOut}
                className="h-10 min-h-10 shrink-0 font-semibold"
              >
                Sign out
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={handleSignIn}
              className="h-10 min-h-10 shrink-0 font-semibold"
            >
              Sign in
            </Button>
          )}
        </div>

        <div className="relative flex sm:hidden">
          <button
            type="button"
            className="rounded-lg p-2 text-white hover:bg-white/10 focus-visible:outline focus-visible:ring-2 focus-visible:ring-sky-400/70"
            aria-label="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <MenuIcon />
          </button>

          {menuOpen ? (
            <div className="absolute right-0 top-full z-50 mt-2 min-w-[12rem] rounded-lg border border-white/10 bg-neutral-950/95 py-1 shadow-xl backdrop-blur-md">
              <button
                type="button"
                className="block w-full px-4 py-2 text-left text-sm text-white hover:bg-white/10"
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/contact");
                }}
              >
                Contact
              </button>
              {loading ? null : isAuthenticated ? (
                <>
                  <button
                    type="button"
                    className="block w-full px-4 py-2 text-left text-sm text-white hover:bg-white/10"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/membership");
                    }}
                  >
                    Membership
                  </button>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-white hover:bg-white/10"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate("/profile");
                    }}
                  >
                    <img
                      src={avatarUrl}
                      alt=""
                      className="h-6 w-6 rounded-full border border-white/20 object-cover"
                    />
                    <span className="truncate">{displayName || user?.email || "Signed in"}</span>
                  </button>
                  <div className="my-1 border-t border-white/10" />
                  <button
                    type="button"
                    className="block w-full px-4 py-2 text-left text-sm text-white hover:bg-white/10"
                    onClick={handleSignOut}
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="block w-full px-4 py-2 text-left text-sm text-white hover:bg-white/10"
                  onClick={handleSignIn}
                >
                  Sign in
                </button>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
